'use client';

import { useEffect, useMemo } from 'react';
import Accordion from '@sympla/sympla-components/Accordion';
import { Container } from 'components/Container/Container.css';
import addEventToDataLayer from 'components/Integration/addEventToDataLayer';

import * as styles from './FAQAccordionCX.css';
import { analytics, analyticsEvents } from 'lib/analytics';
import { useInView } from 'react-intersection-observer';

function FAQAccordionCX() {
  const { ref, inView } = useInView();

  const handleTracking = (label: string) => {
    addEventToDataLayer({
      category: 'FAQ_home',
      label
    });

    analytics.sendEvent(analyticsEvents.faq_clicked, {
      home_faq_option: label
    });
  };

  const itensFAQAccordionCX = useMemo(
    () => [
      {
        title: 'Como eu cancelo ou peço reembolso de ingressos?',
        id: 'cancelamento-e-reembolso',
        content: (
          <>
            <p>
              O reembolso depende do método de pagamento usado na compra e segue
              regras específicas. A Sympla não faz reembolso parcial: se pedir
              reembolso, todos os ingressos do pedido são cancelados.
            </p>
            <p className={styles.TextPaddingTop}>
              Confira como funciona na nossa{' '}
              <a
                href="https://ajuda.sympla.com.br/hc/pt-br/search?query=Cancelamento+ou+reembolso+de+ingressos"
                className={styles.LinkStyle}
                onClick={() =>
                  handleTracking('cancelamento-reembolso-central de ajuda')
                }
              >
                Central de Ajuda
              </a>
              .
            </p>
          </>
        )
      },
      {
        title: 'Como localizar meus ingressos?',
        id: 'localizar-ingressos',
        content: (
          <p>
            Seus ingressos são enviados por e-mail e também ficam disponíveis na
            plataforma. Você pode acessá-los pelo app ou pelo site, mesmo que
            ainda não tenha criado uma conta.
          </p>
        )
      },
      {
        title: 'Como trocar a titularidade do ingresso?',
        id: 'trocar-titularidade',
        content: (
          <>
            <p>
              Em muitos eventos, você pode trocar a titularidade direto pelo
              site, de forma simples e rápida. É só ter uma conta Sympla com o
              mesmo e-mail da compra.
            </p>
            <p className={styles.TextPaddingTop}>
              <strong>Ah, importante:</strong> a troca é válida até 24h antes do
              evento (ou conforme regras do organizador) e só pode ser feita uma
              vez.
            </p>
          </>
        )
      },
      {
        title: 'Como acesso meu certificado?',
        id: 'acessar-certificado',
        content: (
          <>
            <p>
              Participou de um evento e não encontrou seu certificado? Em alguns
              casos, ele é liberado apenas após o check-in ou em uma data
              definida pela organização. Se você já seguiu as orientações e
              mesmo assim não conseguiu acessar, vale confirmar com o produtor
              do evento.
            </p>
            <p className={styles.TextPaddingTop}>
              A emissão e o envio são responsabilidade da organização, mas você
              pode acompanhar tudo pela sua conta na Sympla.
            </p>
          </>
        )
      },
      {
        title: 'Não consigo acessar minha conta, o que fazer?',
        id: 'acessar-conta',
        content: (
          <>
            <p>
              Se você comprou um ingresso, mas não criou uma conta na Sympla,
              não se preocupe: isso é comum. A conta não é criada
              automaticamente no momento da compra.
            </p>
            <p className={styles.TextPaddingTop}>
              Para acessar seus ingressos e informações, use o mesmo e-mail da
              compra e crie uma conta agora. Se já tiver uma conta e não lembra
              a senha, é só redefinir.
            </p>
            <p className={styles.TextPaddingTop}>
              Ah, e não precisa se preocupar: seus ingressos continuam salvos e
              serão recuperados assim que você entrar com o e-mail certo.
            </p>
          </>
        )
      }
    ],
    []
  );

  useEffect(() => {
    if (!inView) return;
    analytics.sendEvent(analyticsEvents.faq_viewed);
  }, [inView]);

  return (
    <section className={styles.Section}>
      <div className={Container} ref={ref}>
        <h3 className={styles.Title}>Precisa de ajuda?</h3>
        <h3 className={`${styles.Title} ${styles.TitleDesktop}`}>
          Tire suas dúvidas aqui
        </h3>
        <Accordion
          items={itensFAQAccordionCX}
          clickItemAccordion={handleTracking}
        />
      </div>
    </section>
  );
}

export default FAQAccordionCX;
